// src/admin/utils/export.js
const REPORT_NAMES = {
  stats_report: "Stats Report",
  users_report: "Users Report",
  organizations_report: "Organizations Report",
  teams_report: "Teams Report",
  opportunity_report: "Opportunity Report",
  reports_report: "Reports Report",
};

const formatTimestamp = () =>
  new Date().toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

const normalizeFieldName = (key = "") =>
  String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const toPrintableValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString("en-US");
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "[Object]";
    }
  }
  return String(value);
};

const buildDivider = (char = "=") => char.repeat(76);

const saveTextFile = (filename, content) => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const saveCsvFile = (filename, csvContent) => {
  const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const IMAGE_KEY_PATTERN = /(image|logo|avatar|photo|cover|banner|screenshot|evidence)/i;

const isImageLikeString = (value) => {
  const str = String(value || "").trim().toLowerCase();
  if (!str) return false;
  if (str.startsWith("data:image/")) return true;
  if (/\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i.test(str)) return true;
  return false;
};

const shouldOmitImageEntry = (key, value) => {
  const lowerKey = String(key || "").toLowerCase();
  if (IMAGE_KEY_PATTERN.test(lowerKey)) return true;
  if (lowerKey.includes("url") && isImageLikeString(value)) return true;
  return false;
};

export const exportTableData = (reportKey, rows = [], options = {}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const readableName = options.reportName || REPORT_NAMES[reportKey] || normalizeFieldName(reportKey);
  const filename = `${reportKey || "admin_report"}.txt`;
  const generatedAt = formatTimestamp();
  const summaryPairs = Object.entries(options.summary || {});

  const lines = [];
  lines.push("VOLUX ADMINISTRATIVE REPORT");
  lines.push(buildDivider("="));
  lines.push(`Report Type : ${readableName}`);
  lines.push(`Generated At: ${generatedAt}`);
  lines.push("Generated By: Volux Admin Dashboard");
  lines.push(buildDivider("="));
  lines.push("");

  lines.push("SUMMARY");
  lines.push(buildDivider("-"));
  lines.push(`Total Records: ${safeRows.length}`);
  summaryPairs.forEach(([key, value]) => {
    lines.push(`${normalizeFieldName(key)}: ${toPrintableValue(value)}`);
  });
  lines.push(buildDivider("-"));
  lines.push("");

  lines.push("DETAILS");
  lines.push(buildDivider("-"));
  if (!safeRows.length) {
    lines.push("No records available for the selected filters.");
  } else {
    safeRows.forEach((row, index) => {
      lines.push(`[${index + 1}]`);
      const entries = Object.entries(row || {}).filter(
        ([key, value]) => value !== undefined && !shouldOmitImageEntry(key, value)
      );
      entries.forEach(([key, value]) => {
        lines.push(`${normalizeFieldName(key)}: ${toPrintableValue(value)}`);
      });
      lines.push(buildDivider("-"));
    });
  }

  lines.push("");
  lines.push("End of Report");

  saveTextFile(filename, lines.join("\n"));
};

export const exportStatsDataAsCsv = (reportKey, stats = {}) => {
  const filename = `${reportKey || "stats_report"}.csv`;
  const generatedAt = formatTimestamp();
  const headers = [
    "Generated At",
    "Total Volunteers",
    "Total Organizations",
    "Total Teams",
    "Total Opportunities",
    "Pending Approvals",
  ];
  const row = [
    generatedAt,
    Number(stats.totalVolunteers || 0),
    Number(stats.totalOrganizations || 0),
    Number(stats.totalTeams || 0),
    Number(stats.totalOpportunities || 0),
    Number(stats.pendingApprovals || 0),
  ];
  const csv = `${headers.join(",")}\n${row.join(",")}`;
  saveCsvFile(filename, csv);
};
